import Multipoint from '@arcgis/core/geometry/Multipoint';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Graphic from '@arcgis/core/Graphic';
import MarkerSymbol from '@arcgis/core/symbols/MarkerSymbol';
import LineSymbol from '@arcgis/core/symbols/LineSymbol';
import FillSymbol from '@arcgis/core/symbols/FillSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';

import { GroundPoint } from './GroundPoint';
import stores from 'cd-eft/models/Stores';

const libraryStore = stores.AppStore.projectsLibraryStore;

const __defaultMarkerSymbol = new SimpleMarkerSymbol();
const __defaultLineSymbol = new SimpleLineSymbol();
const __defaultFillSymbol = new SimpleFillSymbol({ color: 'transparent', outline: { color: 'black', width: 2 } });

export class SurfaceBorder {
  readonly eft_type: string = 'SurfaceBorder';
  readonly borderPoints: GroundPoint[] = [];
  wkid: number = 0;

  constructor() { }

  public generatePointGeometryList(spatialReference?: SpatialReference): Point[] {
    // //ensure has enough points to be a border loop, must have at least 3, plus a duplicate of the first point
    // if (this.borderPoints.length < 3) {
    //   throw new Error('Invalid operation. The border has fewer than 3 points.');
    // }

    // //ensure it is a closed loop
    // if (!this.borderPoints[0].equals(this.borderPoints[this.borderPoints.length - 1])) {
    //   throw new Error('Invalid operation. The border is not a self closing loop.');
    // }

    if (!spatialReference) {
      spatialReference = libraryStore.projectSpatialReference!;
    }

    const pointList: Point[] = [];
    for (const borderPoint of this.borderPoints) {
      const point = borderPoint.generateGeometry(spatialReference);
      pointList.push(point);
    }

    return pointList;
  }

  generateMultipointGeometry(spatialReference?: SpatialReference): Multipoint {
    if (!spatialReference) {
      spatialReference = libraryStore.projectSpatialReference!;
    }

    const pointList = this.generatePointGeometryList(spatialReference);

    const result = new Multipoint();
    result.spatialReference = spatialReference;
    result.hasZ = true;
    for (const point of pointList) {
      result.addPoint(point);
    }
    return result;
  }

  generatePolylineGeometry(spatialReference?: SpatialReference): Polyline {
    if (!spatialReference) {
      spatialReference = libraryStore.projectSpatialReference!;
    }

    const path = this.generatePointGeometryList(spatialReference);

    const result = new Polyline();
    result.spatialReference = spatialReference;
    result.hasZ = true;
    result.addPath(path);

    return result;
  }

  generatePolygonGeometry(spatialReference?: SpatialReference): Polygon {
    if (!spatialReference) {
      spatialReference = libraryStore.projectSpatialReference!;
    }

    const ring = this.generatePointGeometryList(spatialReference);

    const result = new Polygon();
    result.spatialReference = spatialReference;
    result.hasZ = true;
    result.addRing(ring);

    return result;
  }

  generateMultipointGraphic(spatialReference?: SpatialReference, symbol?: MarkerSymbol): Graphic {
    if (!symbol) {
      symbol = __defaultMarkerSymbol;
    }

    const multipoint = this.generateMultipointGeometry(spatialReference);

    const graphic = new Graphic();
    graphic.geometry = multipoint;
    graphic.symbol = symbol;
    graphic.attributes = this;

    return graphic;
  }

  generatePolylineGraphic(spatialReference?: SpatialReference, symbol?: LineSymbol): Graphic {
    if (!symbol) {
      symbol = __defaultLineSymbol;
    }

    const polyline = this.generatePolylineGeometry(spatialReference);

    const graphic = new Graphic();
    graphic.geometry = polyline;
    graphic.symbol = symbol;
    graphic.attributes = this;

    return graphic;
  }

  generatePolygonGraphic(spatialReference?: SpatialReference, symbol?: FillSymbol): Graphic {
    if (!symbol) {
      symbol = __defaultFillSymbol;
    }

    const polygon = this.generatePolygonGeometry(spatialReference);

    const graphic = new Graphic();
    graphic.geometry = polygon;
    graphic.symbol = symbol;
    graphic.attributes = this;

    return graphic;
  }
}
